import Downshift from 'downshift';
import { compose, lifecycle, withProps } from 'recompose';
import { connect } from 'react-redux';
import delve from 'dlv';
import { LinkWithSearchParams as Link } from 'components/base/Link/Link';
import { string, shape, arrayOf, oneOfType, object, bool } from 'prop-types';

import { ReactComponent as KebabMenu } from 'icons/icon-kebab.svg';

import {
  shouldLoadGlossary,
  loadGlossary,
  glossaryPropType,
} from 'redux/reducers/glossary';

import { SquareButton } from './ToolbarDropdown.style';
import List, {
  DropdownListWrapper,
} from 'components/styled/DropdownList/DropdownList';

import stringToSlug from 'helpers/stringToSlug';
import userCanEdit from 'helpers/userCanEdit';
import { logToAmplitude } from 'helpers/logAmplitudeEvent';

/** ToolbarDropdown displays multiple links as a dropdown after clicking on a button */
const ToolbarDropdown = (props) => {
  const {
    glossary,
    glossarySlug,
    extraToolbarItems = [],
    userCanEdit,
    className,
    title,
  } = props;

  const defaultToolbarItems = [];

  if (glossarySlug) {
    const [categorySlug, termSlug] = glossarySlug.split('/');

    const hasGlossaryTerm =
      glossary.success &&
      Boolean(
        delve(
          glossary.data.find((category) => category.slug === categorySlug),
          'topics',
          []
        ).find((term) => term.slug === termSlug)
      );

    if (hasGlossaryTerm || userCanEdit) {
      defaultToolbarItems.push({
        to: {
          pathname: `/glossary/${stringToSlug(glossarySlug)}`,
          state: {
            modal: true,
          },
        },
        label: hasGlossaryTerm ? 'Glossary' : 'Add to glossary',
      });
    }
  }

  const toolbarItems = defaultToolbarItems.concat(extraToolbarItems);
  const sortByAlphabet = (item1, item2) => (item1.label > item2.label ? 1 : -1);
  const toolbarsByAlphabet = toolbarItems.sort(sortByAlphabet);
  const clickOnReference = (item) => {
    if (item?.label?.toLowerCase()?.includes('export') && !!title) {
      logToAmplitude('Export Button Clicked', {
        section: title,
      });
    }
  };

  return toolbarItems?.length ? (
    <Downshift id="ToolbarDropdown">
      {({ isOpen, toggleMenu }) => {
        return (
          <div className={className}>
            <SquareButton
              aria-haspopup="true"
              aria-expanded={isOpen}
              onClick={toggleMenu}
              isOpen={isOpen}
              data-pw="context-menu"
              data-testid="context-menu-button"
            >
              <span>
                <KebabMenu />
              </span>
            </SquareButton>
            {isOpen && (
              <DropdownListWrapper
                overflowLeft
                data-pw="context-menu-list"
                data-testid="context-menu-list"
              >
                <List>
                  {toolbarsByAlphabet.map((item) => {
                    const cyKey = `toolbar-dropdown-item-${stringToSlug(
                      item.label.indexOf('Export') === -1
                        ? item.label
                        : 'Export data'
                    )}`;

                    const key = `toolbar-dropdown-item-${stringToSlug(
                      item.label
                    )}`;

                    if (item.to) {
                      return (
                        <li key={key} data-pw={cyKey}>
                          <Link
                            data-pw={cyKey}
                            data-testid={key}
                            to={item.to}
                            {...item.otherProps}
                          >
                            <span>{item.label}</span>
                          </Link>
                        </li>
                      );
                    } else if (item.href) {
                      return (
                        <li key={key} data-pw={cyKey}>
                          <a
                            data-testid={key}
                            href={item.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => clickOnReference(item)}
                            {...item.otherProps}
                          >
                            <span>{item.label}</span>
                          </a>
                        </li>
                      );
                    } else if (item.onClick) {
                      return (
                        <li key={key} data-pw={cyKey}>
                          <button
                            onClick={() => {
                              item.onClick();
                              toggleMenu();
                            }}
                            {...item.otherProps}
                          >
                            <span>{item.label}</span>
                          </button>
                        </li>
                      );
                    }

                    return null;
                  })}
                </List>
              </DropdownListWrapper>
            )}
          </div>
        );
      }}
    </Downshift>
  ) : null;
};

ToolbarDropdown.propTypes = {
  /** The glossary slug in the API */
  glossarySlug: string,
  /** Array of configuration objects for the Toolbar options */
  extraToolbarItems: arrayOf(
    shape({
      to: oneOfType([object, string]),
      href: string,
      label: string,
      otherProps: object,
    })
  ),
  /** Optional className, required for styled-components composition */
  className: string,
  /** Can this user edit the glossary */
  userCanEdit: bool,
  /** Glossary reducer state */
  glossary: glossaryPropType.isRequired,
};

export default compose(
  connect(
    (state) => ({
      profile: state.authentication.profile,
      glossary: state.glossary,
    }),
    {
      shouldLoadGlossary,
      loadGlossary,
    }
  ),
  lifecycle({
    componentDidMount() {
      if (this.props.shouldLoadGlossary()) {
        this.props.loadGlossary();
      }
    },
  }),
  withProps(({ profile }) => ({
    userCanEdit: userCanEdit(profile),
  }))
)(ToolbarDropdown);
